import { useState } from 'react';
import {
  Container,
  Title,
  Card,
  Text,
  Stack,
  Group,
  Avatar,
  Button,
  Loader,
  Alert,
  Pagination,
  Divider,
} from '@mantine/core';
import { IconBan, IconExclamationCircle, IconUser } from '@tabler/icons-react';
import { useAuth } from '@clerk/react';
import { notifications } from '@mantine/notifications';
import { useApi, useApiClient } from '../api';
import ProtectedRoute from '../components/auth/ProtectedRoute';

const PAGE_LIMIT = 20;

// Blocked-users management (contract §4.13): newest block first, unblock is
// an idempotent DELETE.
const BlockedUsersPage = () => {
  const [page, setPage] = useState(1);
  const { isSignedIn } = useAuth();
  const api = useApiClient();
  const [unblockingId, setUnblockingId] = useState(null);

  const { data, loading, error, refetch } = useApi(
    '/api/v1/me/blocks',
    { page, limit: PAGE_LIMIT },
    { enabled: !!isSignedIn, query: { placeholderData: (prev) => prev } }
  );
  const blocks = data?.items ?? [];
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;

  const handleUnblock = async (userId) => {
    if (unblockingId != null) return;
    setUnblockingId(userId);
    try {
      await api.del(`/api/v1/me/blocks/${userId}`);
      refetch();
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err?.message || 'Could not unblock user',
        color: 'red',
      });
    } finally {
      setUnblockingId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <ProtectedRoute requireAuth={true}>
      <Container size="sm" py="xl">
        <Stack gap="xl">
          <Group gap="xs" align="center">
            <IconBan size="1.5rem" color="var(--mantine-color-gray-6)" />
            <Title order={1}>Blocked Users</Title>
          </Group>

          {error && (
            <Alert icon={<IconExclamationCircle size="1rem" />} color="red">
              {error.message}
            </Alert>
          )}

          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
              <Loader size="lg" />
            </div>
          ) : blocks.length === 0 ? (
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Stack align="center" gap="md" py="xl">
                <IconBan size={48} color="var(--mantine-color-gray-4)" />
                <Text c="dimmed" size="lg">No blocked users</Text>
                <Text c="dimmed" size="sm">
                  People you block can't message you. They'll show up here.
                </Text>
              </Stack>
            </Card>
          ) : (
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Stack gap={0}>
                {blocks.map((block, index) => (
                  <div key={block.user_id}>
                    {index > 0 && <Divider my="sm" />}
                    <Group justify="space-between" wrap="nowrap">
                      <Group gap="sm" wrap="nowrap">
                        <Avatar size="md" color="gray" src={block.avatar_url}>
                          <IconUser size="1.5rem" />
                        </Avatar>
                        <div>
                          <Text fw={500}>{block.display_name}</Text>
                          <Text size="xs" c="dimmed">
                            Blocked {formatDate(block.blocked_at)}
                          </Text>
                        </div>
                      </Group>
                      <Button
                        variant="light"
                        color="gray"
                        size="xs"
                        leftSection={<IconBan size="0.9rem" />}
                        onClick={() => handleUnblock(block.user_id)}
                        loading={unblockingId === block.user_id}
                      >
                        Unblock
                      </Button>
                    </Group>
                  </div>
                ))}
              </Stack>
            </Card>
          )}

          {totalPages > 1 && (
            <Group justify="center">
              <Pagination value={page} onChange={setPage} total={totalPages} />
            </Group>
          )}
        </Stack>
      </Container>
    </ProtectedRoute>
  );
};

export default BlockedUsersPage;
